import { getRouteApi } from '@tanstack/react-router';
import { useMemo, useState } from 'react';

import { Botao } from '@/componentes/Botao';
import { ChaveDeFiltro } from '@/componentes/ChaveDeFiltro';
import { Barra } from '@/componentes/Esqueleto';
import { EstadoVazio } from '@/componentes/EstadoVazio';
import { Medida } from '@/componentes/Medida';
import { RodapeTabela, Tabela, TextoDaCelula, type Coluna } from '@/componentes/Tabela';
import { formatarDataHora, formatarInteiro, formatarReais } from '@/lib/formato';
import { useSessaoEmMemoria } from '@/modulos/sessao/memoria';

import { OPCOES_POR_PAGINA, PADRAO_POR_PAGINA, montarBuscaDaLista } from './busca';
import { useAbastecimentos } from './consultas';
import { DetalheDoAbastecimento, EtiquetaCorrigido, SeloEstornado } from './DetalheDoAbastecimento';
import { JanelasDoAbastecimento } from './EstornoDoAbastecimento';
import { juntarNaPosicaoOriginal } from './estorno';
import { HubDoCombustivel } from './HubDoCombustivel';
import { DECISOES_EM_ABERTO } from './decisoes';
import { MenuDaLinhaDoAbastecimento, type AcaoNaLinhaDoAbastecimento } from './MenuDoAbastecimento';
import { TEXTO_DO_CANAL, canalDoRegistro, maquinaPorExtenso, quemLancou } from './registro';
import type { Abastecimento } from './tipos';
import { gastoDosAbastecimentos, totalDoAbastecimento } from './tipos';
import { litrosEscritos } from './litros';

const rota = getRouteApi('/combustivel');

/**
 * Uma linha da lista principal: o lançamento que vale ou o estornado que a
 * chave "Mostrar estornados" trouxe de volta à posição original (R06, p.69).
 */
interface LinhaDaLista {
  readonly abastecimento: Abastecimento;
  readonly estornado: boolean;
  /** O motivo do estorno que esta linha corrige, para a etiqueta "Corrigido". */
  readonly motivoDaCorrecao: string | null | undefined;
}

/** Toda soma da lista é só do que vale: o estornado aparece, e não soma. */
const valem = (linhas: readonly LinhaDaLista[]) => linhas.filter((l) => !l.estornado);

/* Cada linha de texto em `TextoDaCelula` (p.18): nome, obra e origem vão a
   duas linhas, ou a uma com reticências e o texto inteiro no `title`, em vez
   de a célula presa numa linha só somar a frase mais longa de cada coluna. As
   sete colunas pediam 985 px, e a área útil em L (1280) tem 966: a tabela
   rolava dentro do quadro já em L, e em M o "Total" aparecia com 28 dos 111
   px. Data com hora não quebra nunca: "25/09/2026 ·" em cima de "19:01"
   deixa de ser uma data.

   OITO COLUNAS, o máximo da p.27 (27/10/2026): o dono pediu a máquina por
   extenso e quem lançou. A máquina entrou como SEGUNDA LINHA do código (12 px,
   p.26), e não como coluna; "Lançado por" entrou no fim da linha, antes dos
   três pontos. O resto do lançamento — operador, responsável, centro de custo,
   medidor, comprovante — está no painel de detalhe. */
const COLUNAS: readonly Coluna<LinhaDaLista>[] = [
  {
    chave: 'equipamento',
    rotulo: 'Equipamento',
    celula: ({ abastecimento, estornado }) => (
      <>
        <span className={`codigo block whitespace-nowrap ${estornado ? 'text-tinta-apagada line-through' : 'text-tinta'}`}>
          {abastecimento.equipamentoCodigo}
        </span>
        {/* A MÁQUINA POR EXTENSO (pedido do dono): "BAS-001" sozinho não diz
            qual caminhão é. A descrição e o tipo, numa linha, com o texto
            inteiro no `title`. */}
        <LinhaUnica
          texto={maquinaPorExtenso(abastecimento.equipamentoDescricao, abastecimento.equipamentoTipo) ?? 'Sem descrição cadastrada'}
          classe="txt-linha2 max-w-[20ch] min-[1440px]:max-w-[30ch] text-tinta-apagada"
        />
      </>
    ),
  },
  {
    chave: 'quando',
    rotulo: 'Quando',
    quebra: 'nunca',
    /* A SITUAÇÃO mora aqui, na segunda linha, como na tela de lançamento: o
       estornado com o selo (nunca só a cor) e o que corrigiu outro com a
       etiqueta âmbar e o motivo ao passar o mouse. */
    celula: ({ abastecimento, estornado, motivoDaCorrecao }) => (
      <>
        <span className={`block whitespace-nowrap ${estornado ? 'text-tinta-apagada' : 'text-tinta'}`}>{formatarDataHora(abastecimento.quando)}</span>
        {estornado ? (
          <SeloEstornado
            titulo={abastecimento.estorno ? `${abastecimento.estorno.por ?? 'Alguém'} estornou em ${formatarDataHora(abastecimento.estorno.em)}` : undefined}
          />
        ) : abastecimento.corrigeId ? (
          <EtiquetaCorrigido motivo={motivoDaCorrecao ?? null} />
        ) : null}
      </>
    ),
  },
  {
    chave: 'origem',
    rotulo: 'Origem',
    /* Interno e externo na MESMA coluna, com o nome do tanque ou do posto: são
       o mesmo fato (combustível entrou na máquina) com lastro diferente, e o
       lastro é o que muda a conferência. */
    /* O POSTO EM UMA LINHA, como o `linhas={1}` de antes já pedia e não
       conseguia (ver `LinhaUnica`): com as duas colunas novas, "POSTO TREVO —
       ANÁPOLIS" quebrava em duas e a linha do externo ia a três, 69 px em 1280
       contra os 51 do HEAD (medido na prova pela tela). */
    celula: ({ abastecimento, estornado }) => {
      const tinta = estornado ? 'text-tinta-apagada' : 'text-tinta';
      return abastecimento.posto ? (
        <>
          <LinhaUnica texto={abastecimento.posto} classe={`txt-corpo max-w-[28ch] ${tinta}`} />
          <LinhaUnica
            texto={`Posto${abastecimento.comprovante ? ` · ${abastecimento.comprovante}` : ''}`}
            classe="txt-linha2 max-w-[28ch] text-tinta-apagada"
          />
        </>
      ) : (
        <TextoDaCelula texto={abastecimento.tanque ?? 'Não informada'} classe={`txt-corpo ${tinta}`} />
      );
    },
  },
  {
    chave: 'obra',
    rotulo: 'Obra',
    /* SECUNDÁRIA desde 27/10/2026 (R01, p.28): sai abaixo de 1280 e com o
       painel aberto, e continua no painel ("Obra", na aba Lançamento). Com a
       máquina por extenso e "Lançado por", as oito colunas em M davam 91 px à
       Origem e a linha ia a 123 px; em M ficam as colunas-chave — máquina,
       quando, origem, litros, total e quem lançou. */
    prioridade: 'secundaria',
    celula: ({ abastecimento, estornado }) => (
      <TextoDaCelula texto={abastecimento.obra ?? 'Sem obra'} {...(estornado ? { classe: 'text-tinta-apagada' } : {})} />
    ),
  },
  {
    chave: 'litros',
    rotulo: 'Litros',
    alinhamento: 'numero',
    celula: ({ abastecimento, estornado }) =>
      estornado ? (
        <span className="numero text-tinta-apagada line-through">{`${litrosEscritos(abastecimento.litros)} L`}</span>
      ) : (
        <Medida valor={litrosEscritos(abastecimento.litros)} unidade="L" />
      ),
    total: (linhas) => (
      <Medida valor={litrosEscritos(valem(linhas).reduce((soma, l) => soma + l.abastecimento.litros, 0))} unidade="L" />
    ),
  },
  {
    chave: 'preco',
    rotulo: 'Preço/litro',
    alinhamento: 'numero',
    /* Secundária (R01, p.28): sai abaixo de 1280 e dos cartões. O total da
       linha fica, e a média do litro está escrita logo acima da tabela. */
    prioridade: 'secundaria',
    celula: ({ abastecimento, estornado }) =>
      abastecimento.precoLitro === null ? (
        <span className="numero text-tinta-secundaria">Sem preço</span>
      ) : estornado ? (
        <span className="numero text-tinta-apagada">{`${formatarReais(abastecimento.precoLitro)}/L`}</span>
      ) : (
        <Medida valor={formatarReais(abastecimento.precoLitro)} unidade="/L" colado />
      ),
    /* Sem soma nesta coluna: média simples de preços com volumes diferentes é
       um número errado com cara de certo. O preço médio real é o gasto dividido
       pelos litros, e ele já está no indicador acima. */
  },
  {
    chave: 'total',
    rotulo: 'Total',
    alinhamento: 'numero',
    celula: ({ abastecimento, estornado }) => {
      const total = totalDoAbastecimento(abastecimento);
      if (total === null) return <span className="numero text-tinta-secundaria">—</span>;
      return estornado ? (
        <span className="numero text-tinta-apagada line-through">{formatarReais(total)}</span>
      ) : (
        <span className="text-tinta">{formatarReais(total)}</span>
      );
    },
    total: (linhas) => (
      <span className="text-tinta">
        {formatarReais(valem(linhas).reduce((soma, l) => soma + (totalDoAbastecimento(l.abastecimento) ?? 0), 0))}
      </span>
    ),
  },
  {
    chave: 'lancadoPor',
    rotulo: 'Lançado por',
    /* QUEM REGISTROU (pedido do dono, 27/10/2026), lido da trilha — e não o
       operador nem o responsável, que são dados que quem lança escolhe
       (`quemLancou`, registro.ts). Embaixo, o perfil de quem lançou; sem
       autor na trilha, o canal por onde a linha entrou. */
    celula: ({ abastecimento, estornado }) => {
      const temAutor = Boolean(abastecimento.registro?.por);
      const segunda = temAutor
        ? (abastecimento.registro?.perfil ?? 'Sem papel em vigor')
        : TEXTO_DO_CANAL[canalDoRegistro(abastecimento)];
      return (
        <>
          <LinhaUnica
            texto={quemLancou(abastecimento)}
            classe={`txt-corpo max-w-[18ch] ${estornado ? 'text-tinta-apagada' : temAutor ? 'text-tinta' : 'text-tinta-secundaria'}`}
          />
          <LinhaUnica texto={segunda} classe="txt-linha2 max-w-[18ch] text-tinta-apagada" />
        </>
      );
    },
  },
];

/**
 * UMA LINHA, com reticências e o texto inteiro no `title` (p.18) — para as
 * duas células novas de 27/10/2026, que têm de caber nos 48 px da linha dupla.
 *
 * Não é o `TextoDaCelula` com `linhas={1}` porque, nele, o `block` da mesma
 * lista de classes vence o `display` do `line-clamp-1`, e o corte não acontece:
 * "SÉRGIO BASTOS" quebrava em duas linhas e a célula de quem lançou ia a três
 * (medido na prova pela tela, 27/10/2026). O defeito é do componente
 * compartilhado e foi relatado, não consertado aqui: consertá-lo muda o corte
 * de toda tabela do sistema.
 */
function LinhaUnica({ texto, classe }: { readonly texto: string; readonly classe: string }) {
  return (
    <span className={`block truncate ${classe}`} title={texto}>
      {texto}
    </span>
  );
}

export function PaginaCombustivel() {
  const busca = rota.useSearch();
  const navegarNaLista = rota.useNavigate();
  const consulta = useAbastecimentos();
  const sessao = useSessaoEmMemoria();
  /* A ação aberta nos três pontos. O menu e as janelas são os MESMOS da tela
     de lançamento (`MenuDoAbastecimento.tsx`): corrigir e estornar com a
     licença de estorno; apagar só com `abastecimento.excluir`, do
     administrador. */
  const [acaoNaLinha, setAcaoNaLinha] = useState<AcaoNaLinhaDoAbastecimento | null>(null);
  /* `linhas` e o que o servidor devolveu; `total` e quanto existe. Os dois
     numeros sao diferentes sempre que a empresa passa do limite do servidor, e
     a tela escreve o recorte ao lado de cada soma — sem isso, "Volume" e a
     soma de 200 linhas apresentada como se fosse a soma de todas. */
  const abastecimentos = useMemo(() => consulta.data?.linhas ?? [], [consulta.data]);
  const estornados = useMemo(() => consulta.data?.estornados ?? [], [consulta.data]);
  const total = consulta.data?.total ?? abastecimentos.length;
  const cortada = total > abastecimentos.length;
  const recorte = cortada
    ? `os ${formatarInteiro(abastecimentos.length)} mais recentes, de ${formatarInteiro(total)}`
    : 'todos os lançamentos da empresa';
  /* R06 (p.69): o estornado fica oculto atrás da chave "Mostrar estornados". */
  const [mostrarEstornados, setMostrarEstornados] = useState(false);
  /* A linha aberta no painel. Guarda o ID, e não a linha: a lista chega
     outra vez a cada invalidação (estornar, corrigir), e um objeto guardado
     ficaria sendo a versão de antes da ação. */
  const [linhaAberta, setLinhaAberta] = useState<string | null>(null);

  const litros = abastecimentos.reduce((soma, a) => soma + a.litros, 0);
  /* O preço médio divide o gasto pelos litros DOS MESMOS lançamentos. Com os
     litros de todos no denominador, os sem preço derrubavam a média: R$ 1,67
     o litro numa lista em que o mais barato custou R$ 6,24. */
  const { gasto, semPreco, precoMedio } = gastoDosAbastecimentos(abastecimentos);
  const somaDaLista =
    litros === 0
      ? ''
      : precoMedio === null
        ? `${litrosEscritos(litros)} L, ${formatarReais(gasto)}. `
        : `${litrosEscritos(litros)} L, ${formatarReais(gasto)}; ${formatarReais(precoMedio)} por litro na média ponderada${semPreco > 0 ? ' dos que têm preço' : ''}. `;

  /* Todos os lançamentos carregados, pelo id: o painel acha neles o outro lado
     de uma correção, esteja a chave dos estornados ligada ou não. */
  const porId = useMemo(
    () => new Map<string, Abastecimento>([...abastecimentos, ...estornados].map((a) => [a.id, a])),
    [abastecimentos, estornados],
  );

  const linhasDaLista = useMemo<readonly LinhaDaLista[]>(() => {
    const vigentes = abastecimentos.map(
      (a): LinhaDaLista => ({
        abastecimento: a,
        estornado: false,
        motivoDaCorrecao: a.corrigeId ? (porId.get(a.corrigeId)?.estorno?.motivo ?? null) : undefined,
      }),
    );
    if (!mostrarEstornados) return vigentes;
    const anulados = estornados.map((a): LinhaDaLista => ({ abastecimento: a, estornado: true, motivoDaCorrecao: undefined }));
    return juntarNaPosicaoOriginal(vigentes, anulados, (l) => l.abastecimento.quando);
  }, [abastecimentos, estornados, mostrarEstornados, porId]);

  /* Paginação, como nas outras tabelas do sistema. Em produção esta é a lista
     de abastecimentos da frota inteira, e uma página só não é leitura, é
     rolagem. */
  const pagina = busca.pagina ?? 1;
  const porPagina = busca.porPagina ?? PADRAO_POR_PAGINA;
  const paginas = Math.max(Math.ceil(linhasDaLista.length / porPagina), 1);
  const paginaSegura = Math.min(pagina, paginas);
  const inicio = (paginaSegura - 1) * porPagina;
  const visiveis = linhasDaLista.slice(inicio, inicio + porPagina);

  function irPara(estado: { readonly pagina: number; readonly porPagina: number }) {
    void navegarNaLista({ search: montarBuscaDaLista(estado) });
  }

  /* A linha do painel sai da lista RECÉM-CHEGADA, pelo id. Apagada, ela some
     da lista e o painel fecha sozinho: a linha apagada não tem detalhe. */
  const aberta = linhaAberta === null ? null : (porId.get(linhaAberta) ?? null);

  return (
    <div className="flex min-h-0 min-w-0 flex-1">
      <HubDoCombustivel>
        {/* Só fato bruto: quantos lançamentos, quantos litros, quanto saiu do
            caixa. Os totais estão nos indicadores do hub; aqui fica o que eles
            não dizem — o recorte da lista, o que entrou sem preço e a média do
            litro. Nada de "gasto por obra": o rateio do diesel é decisão do dono
            e ainda não foi tomada. Carregando, a linha é esqueleto (p.32) — "0
            lançamentos" antes da resposta é um número errado; quem anuncia a
            espera é a tabela. */}
        {consulta.isPending ? (
          <Barra className="my-1 h-3 w-3/5" />
        ) : (
          <p className="txt-apoio text-pretty text-tinta-secundaria">
            {`${formatarInteiro(abastecimentos.length)} lançamentos: ${recorte}. `}
            {somaDaLista}
            {semPreco > 0 ? `${formatarInteiro(semPreco)} entraram sem preço e ficam fora do gasto.` : ''}
          </p>
        )}

        {/* As decisões em aberto são do PROJETO, não da obra: numeração interna
            de módulo e "a regra é do dono" não dizem nada a quem lança diesel
            (p.22: a palavra da obra vence a palavra do software). Só a sessão de
            suporte da plataforma as vê — a lista continua registrada em
            `decisoes.ts`, que é o único lugar onde duas delas estão escritas. */}
        {sessao?.suporte === true ? (
          <div className="cartao flex flex-col gap-2 p-4">
            <span className="txt-rotulo text-tinta-secundaria">Decisões em aberto</span>
            <ul className="flex list-disc flex-col gap-1 pl-4">
              {DECISOES_EM_ABERTO.map((decisao) => (
                <li key={decisao} className="txt-apoio text-tinta-secundaria">
                  {decisao}
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {/* A chave dos estornados (R06, p.69), no cabeçalho da lista; só quando há estornado. */}
        {estornados.length > 0 ? (
          <div className="flex justify-end">
            <ChaveDeFiltro
              rotulo={`Mostrar estornados · ${formatarInteiro(estornados.length)}`}
              ligada={mostrarEstornados}
              aoMudar={setMostrarEstornados}
            />
          </div>
        ) : null}

        {/* A LISTA É A ORIGEM DO PAINEL: clicar em outra linha troca o detalhe
            em vez de fechá-lo (p.28). */}
        <div data-painel-origem>
          <Tabela
            rotulo="Abastecimentos lançados"
            colunas={COLUNAS}
            linhas={visiveis}
            alturaDupla
            chaveDaLinha={(linha) => linha.abastecimento.id}
            carregando={consulta.isPending}
            rotuloDoTotal="Soma da página"
            selecionada={linhaAberta}
            aoSelecionar={(linha) => {
              setLinhaAberta(linha.abastecimento.id);
            }}
            /* Os três pontos, fixos no canto direito (decisão 40). */
            menu={(linha) => (
              <MenuDaLinhaDoAbastecimento
                abastecimento={linha.abastecimento}
                estornado={linha.estornado}
                aoEscolher={(escolha) => {
                  setAcaoNaLinha(escolha);
                }}
              />
            )}
            erro={
              consulta.isError ? (
                <EstadoVazio
                  titulo="Não foi possível carregar os abastecimentos"
                  descricao="A lista não chegou. Tente carregar de novo; se continuar, avise o suporte com o horário em que aconteceu."
                  acao={
                    <Botao
                      nivel="secundario"
                      onClick={() => {
                        void consulta.refetch();
                      }}
                    >
                      Carregar os abastecimentos
                    </Botao>
                  }
                />
              ) : null
            }
            vazio={
              estornados.length > 0 && !mostrarEstornados ? (
                /* Só há estornados: a lista não está vazia, está recolhida. */
                <EstadoVazio
                  titulo="Só há abastecimentos estornados"
                  descricao="Ligue Mostrar estornados para ver os lançamentos anulados, ou lance um abastecimento novo."
                />
              ) : (
                <EstadoVazio
                  titulo="Nenhum abastecimento lançado"
                  descricao="Lance o primeiro abastecimento para começar a acompanhar o consumo da frota."
                />
              )
            }
            rodape={
              <RodapeTabela
                primeiro={inicio + 1}
                ultimo={inicio + visiveis.length}
                total={linhasDaLista.length}
                pagina={paginaSegura}
                paginas={paginas}
                porPagina={porPagina}
                opcoesPorPagina={OPCOES_POR_PAGINA}
                aoMudarPagina={(nova) => {
                  irPara({ pagina: nova, porPagina });
                }}
                aoMudarPorPagina={(novo) => {
                  irPara({ pagina: 1, porPagina: novo });
                }}
              />
            }
          />
        </div>

        <JanelasDoAbastecimento
          acao={acaoNaLinha?.acao ?? null}
          alvo={acaoNaLinha?.alvo ?? null}
          aoFechar={() => {
            setAcaoNaLinha(null);
          }}
          /* Estornar, de dentro da correção: a janela vira a confirmação (p.69).
             Só existe com a correção aberta, então a ação aberta nunca é nula
             aqui — e é lendo o estado, e não pela forma funcional da tela de
             lançamento, que a análise do H54 acha o portão nas duas páginas. */
          aoTrocarParaEstorno={() => {
            if (acaoNaLinha !== null) setAcaoNaLinha({ acao: 'estorno', alvo: acaoNaLinha.alvo });
          }}
        />
      </HubDoCombustivel>

      {aberta ? (
        <DetalheDoAbastecimento
          key={aberta.id}
          abastecimento={aberta}
          porId={porId}
          aoAbrirOutro={(id) => {
            /* O outro lado da correção pode ser um estornado: a chave liga
               para a linha aparecer marcada na lista. */
            if (porId.get(id)?.estorno) setMostrarEstornados(true);
            setLinhaAberta(id);
          }}
          aoFechar={() => {
            setLinhaAberta(null);
          }}
        />
      ) : null}
    </div>
  );
}
